// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState, useEffect, useContext } from 'react'
import {
  Box,
  FormField,
  Text,
  Notification,
  RadioButtonGroup,
  TextInput,
  CheckBoxGroup
} from 'grommet'
import { Trans, useTranslation } from 'react-i18next'
import lodashGet from 'lodash/get'
import { useNavigate } from 'react-router-dom'

import { WizardContext } from '../../../../../components/wizard/WizardContext'
import { Anchor, Dropdown, Typography } from '../../../../../components'
import { get, getErrorMessage } from '../../../../../utils/api-utils'
import { displayNotification } from '../../../../../utils/notificiation-utils'
import {
  authorizationModeEnum,
  policyTypeEnum,
  ssoConnectionTypeEnum
} from '../../../../../utils/constants'
import { isMSP } from '../../../../../utils/feature-flag-utils'

import { validateGeneralFields } from './utils/validation-utils'

const General = () => {
  const navigate = useNavigate()
  const { t } = useTranslation(['iam'])
  const [domains, setDomains] = useState([])
  const [ssoConnections, setSsoConnections] = useState([])
  const { attemptedAdvance, formValues, setFormValues } =
    useContext(WizardContext)
  const [fieldsError, setFieldsError] = useState({
    mspApplication: '',
    policyType: '',
    domain: '',
    ssoConnections: '',
    ssoConnectionID: '',
    authorizationMode: '',
    acknowledgement: ''
  })

  const authorizationMode = [
    {
      label: t(
        'sso_configurations.create_auth_policy_wizard.local_role_assignments'
      ),
      value: authorizationModeEnum.AUTHENTICATION_ONLY_MODE
    },
    {
      label: t(
        'sso_configurations.create_auth_policy_wizard.sso_role_assignments'
      ),
      value: authorizationModeEnum.AUTHORIZATION_MODE
    }
  ]

  const domainTypes = [
    {
      label: (
        <Box>
          <Text>
            {t('sso_configurations.create_auth_policy_wizard.verified_domain')}
          </Text>
          <Text size="xsmall">
            {t(
              'sso_configurations.create_auth_policy_wizard.verified_domain_description'
            )}
          </Text>
        </Box>
      ),
      value: policyTypeEnum.DOMAIN_VERIFIED
    },
    {
      label: (
        <Box>
          <Text>
            {t('sso_configurations.create_auth_policy_wizard.external_domain')}
          </Text>
          <Text size="xsmall">
            {t(
              'sso_configurations.create_auth_policy_wizard.external_domain_description'
            )}
          </Text>
        </Box>
      ),
      value: policyTypeEnum.DOMAIN_EXTERNAL
    }
  ]

  // Notifications
  const [notifTitle, setNotifTitle] = useState('')
  const [notifMsg, setNotifMsg] = useState('')
  const [notifSeverity, setNotifSeverity] = useState('')
  const setNotificationInfo = (message, severity, title = '') => {
    setNotifTitle(title)
    setNotifMsg(message)
    setNotifSeverity(severity)
  }

  // The list of domains includes only unclaimed domains. In the edit scenario, we need to include the current domain (which is already claimed) in the list.
  const getDomains = () => {
    const domainList = domains
    if (formValues.currentDomain) {
      const domainExists = domainList.find(
        (item) => item.name === formValues.currentDomain
      )
      if (!domainExists) {
        const domain = {
          id: formValues.currentDomain,
          name: formValues.currentDomain
        }
        domainList.push(domain)
      }
    }
    return domainList
  }

  useEffect(() => {
    get('/identity/v2alpha1/sso-profiles').then(
      (response) => {
        setSsoConnections(response?.data?.items)
      },
      (error) => {
        setNotificationInfo(getErrorMessage(error, t), 'error')
      }
    )
  }, [t])

  useEffect(() => {
    let isMounted = true
    const fetchUnclaimedDomains = async () => {
      try {
        const response = await get(
          `/internal-platform-tenant-ui/v2/unclaimed-domains`
        )
        if (isMounted) {
          setDomains(response?.data?.items)
        }
      } catch (error) {
        if (isMounted) {
          setNotificationInfo(getErrorMessage(error, t), 'error')
          setDomains([])
        }
      }
    }

    fetchUnclaimedDomains()

    return () => {
      isMounted = false
    }
  }, [t, formValues])

  useEffect(() => {
    if (attemptedAdvance) {
      setFieldsError(validateGeneralFields(formValues, t))
    }
  }, [formValues, attemptedAdvance, t])

  return (
    <Box gap="medium" pad={{ top: 'xxsmall', bottom: 'medium' }}>
      <Box width="large" gap="medium">
        <Box gap="xsmall">
          {isMSP() && (
            <>
              <Text
                size="xsmall"
                margin={{ top: 'xxsmall' }}
                weight={500}
                data-testid="msp-checkbox-title"
              >
                {t(
                  'sso_configurations.create_auth_policy_wizard.msp_checkbox_title'
                )}
                *
              </Text>
              <Text size="xsmall" data-testid="msp-checkbox-description">
                {t(
                  'sso_configurations.create_auth_policy_wizard.msp_checkbox_description'
                )}
              </Text>
              <FormField
                htmlFor="msp_application"
                name="msp_application_type"
                data-testid="msp-application-form-field"
                required
                error={fieldsError.mspApplication}
              >
                <CheckBoxGroup
                  options={[
                    t(
                      'sso_configurations.create_auth_policy_wizard.checkbox_option_1'
                    ),
                    t(
                      'sso_configurations.create_auth_policy_wizard.checkbox_option_2'
                    )
                  ]}
                  defaultValue={lodashGet(formValues, 'general.mspApplication')}
                  onChange={(event) => {
                    setFormValues({
                      ...formValues,
                      general: {
                        ...formValues.general,
                        mspApplication: event.value
                      }
                    })
                  }}
                />
              </FormField>
            </>
          )}
          <Text
            size="xsmall"
            margin={{ top: 'xxsmall' }}
            weight={500}
            data-testid="policy-type-title"
          >
            {t('sso_configurations.create_auth_policy_wizard.domain_type')}*
          </Text>
          <FormField
            htmlFor="policy_type"
            name="policy_type"
            data-testid="policy-type-form-field"
            required
            error={fieldsError.policyType}
          >
            <RadioButtonGroup
              name="policy_type"
              options={domainTypes}
              value={lodashGet(formValues, 'general.policyType')}
              defaultValue={lodashGet(formValues, 'general.policyType')}
              onChange={(e) => {
                if (e.target.value === policyTypeEnum.DOMAIN_VERIFIED) {
                  setFormValues({
                    ...formValues,
                    general: {
                      ...formValues.general,
                      domain: '',
                      ssoConnections: '',
                      policyType: e.target.value
                    }
                  })
                } else {
                  setFormValues({
                    ...formValues,
                    general: {
                      ...formValues.general,
                      policyType: e.target.value,
                      domain: '',
                      acknowledgement: [],
                      ssoConnectionType: ssoConnectionTypeEnum.EXTERNAL,
                      ssoConnections: ssoConnectionTypeEnum.EXTERNAL,
                      authorizationMode:
                        formValues?.general?.authorizationMode ||
                        authorizationMode[0].value
                    }
                  })
                }
              }}
              data-testid="policy-type-input"
            />
          </FormField>
          {lodashGet(formValues, 'general.policyType') ===
            policyTypeEnum.DOMAIN_EXTERNAL && (
            <Notification
              status="info"
              message={
                <Typography
                  type="text"
                  testId="create-authentication-policies-wizard-notification"
                >
                  {t(
                    'sso_configurations.create_auth_policy_wizard.external_domain_notification'
                  )}
                  <ul style={{ marginBlock: '0px' }}>
                    <li>
                      {t(
                        'sso_configurations.create_auth_policy_wizard.notification_bullet_1'
                      )}
                    </li>
                    <li>
                      {t(
                        'sso_configurations.create_auth_policy_wizard.notification_bullet_2'
                      )}
                    </li>
                    <li>
                      {t(
                        'sso_configurations.create_auth_policy_wizard.notification_bullet_3'
                      )}
                    </li>
                  </ul>
                </Typography>
              }
            />
          )}
          {lodashGet(formValues, 'general.policyType') && (
            <>
              <Text
                size="xsmall"
                margin={{ top: 'xxsmall' }}
                weight={500}
                data-testid="domain-dropdown-title"
              >
                {t('iam:sso_profiles.view_edit_create.domain')}*
              </Text>
              <Text size="xsmall" data-testid="domain-dropdown-subtitle">
                {t(
                  'iam:sso_configurations.create_auth_policy_wizard.domain_dropdown_subtitle'
                )}
              </Text>
              <FormField
                htmlFor="domain"
                name="domain"
                data-testid="domain-form-field"
                required
                error={fieldsError.domain}
              >
                {lodashGet(formValues, 'general.policyType') ===
                policyTypeEnum.DOMAIN_VERIFIED ? (
                  <Dropdown
                    options={getDomains()}
                    clear={{
                      label: t(
                        'iam:sso_profiles.view_edit_create.clear_selection'
                      )
                    }}
                    placeholder={t('iam:sso_profiles.view_edit_create.select')}
                    testId="domain-dropdown"
                    labelKey="name"
                    valueKey="name"
                    value={formValues.general.domain}
                    onChangeDropdown={(values) => {
                      setFormValues({
                        ...formValues,
                        general: { ...formValues.general, domain: values?.name }
                      })
                    }}
                  />
                ) : (
                  <TextInput
                    data-testid="domain-name-input"
                    defaultValue={formValues.general.domain}
                    // name="domainName"
                    onChange={(e) => {
                      if (e.target.value) {
                        setFormValues({
                          ...formValues,
                          general: {
                            ...formValues.general,
                            domain: e.target.value
                          }
                        })
                      }
                    }}
                    maxLength={255}
                  />
                )}
              </FormField>
              {lodashGet(formValues, 'general.policyType') ===
                policyTypeEnum.DOMAIN_VERIFIED && (
                <Notification
                  margin={{ top: 'small', bottom: 'small' }}
                  status="info"
                  message={
                    <Typography
                      type="text"
                      testId="notification-domain-not-listed"
                    >
                      <Trans
                        i18nKey="iam:sso_profiles.view_edit_create.domain_not_listed"
                        t={t}
                      >
                        <Anchor
                          label={t(
                            'iam:sso_profiles.view_edit_create.domain_page'
                          )}
                          weight="bold"
                          onClick={() =>
                            navigate('/manage-account/organization/domains')
                          }
                        />
                      </Trans>
                    </Typography>
                  }
                />
              )}

              {lodashGet(formValues, 'general.policyType') ===
                policyTypeEnum.DOMAIN_VERIFIED && (
                <>
                  <Text
                    size="xsmall"
                    margin={{ top: 'xxsmall' }}
                    weight={500}
                    data-testid="sso-connections-dropdown-title"
                  >
                    {t('sso_configurations.sso_connections_tab')}*
                  </Text>
                  <Text
                    size="xsmall"
                    data-testid="sso-connections-dropdown-description"
                  >
                    {t(
                      'sso_configurations.create_auth_policy_wizard.sso_connections_description'
                    )}
                  </Text>
                  <FormField
                    htmlFor="ssoConnections"
                    name="ssoConnections"
                    data-testid="sso-connections-form-field"
                    required
                    error={fieldsError.ssoConnections}
                  >
                    <Dropdown
                      options={ssoConnections}
                      clear={{
                        label: t(
                          'iam:sso_profiles.view_edit_create.clear_selection'
                        )
                      }}
                      placeholder={t(
                        'iam:sso_profiles.view_edit_create.select'
                      )}
                      testId="sso-connections-dropdown"
                      labelKey="name"
                      valueKey="name"
                      value={formValues.general.ssoConnections}
                      onChangeDropdown={(values) => {
                        setFormValues({
                          ...formValues,
                          general: {
                            ...formValues.general,
                            ssoConnectionType:
                              ssoConnectionTypeEnum.SSO_PROFILE,
                            ssoConnections: values?.name,
                            ssoConnectionID: values?.id,
                            isOIDC: !!values?.oidcIdpConfig,
                            authorizationMode:
                              formValues.general.authorizationMode ||
                              authorizationMode[0].value
                          }
                        })
                      }}
                    />
                  </FormField>
                </>
              )}

              {(lodashGet(formValues, 'general.ssoConnections') ||
                lodashGet(formValues, 'general.policyType') ===
                  policyTypeEnum.DOMAIN_EXTERNAL) && (
                <>
                  {lodashGet(formValues, 'general.isOIDC') && (
                    <Notification
                      margin={{ top: 'small', bottom: 'small' }}
                      status="info"
                      message={
                        <Typography type="text" testId="oidc-notification">
                          The selected OIDC connection only supports local role
                          assignments.
                        </Typography>
                      }
                    />
                  )}
                  <FormField
                    htmlFor="authorization_mode"
                    name="authorizationMode"
                    label={`${t(
                      'sso_configurations.create_auth_policy_wizard.authorization_mode'
                    )}*`}
                    data-testid="authorization-mode-form-field"
                    error={fieldsError.authorizationMode}
                  >
                    <RadioButtonGroup
                      name="authorization_mode"
                      options={
                        lodashGet(formValues, 'general.isOIDC')
                          ? authorizationMode.splice(0, 1)
                          : authorizationMode
                      }
                      value={formValues.general.authorizationMode}
                      defaultValue={
                        formValues.general.authorizationMode ||
                        authorizationMode[0].value
                      }
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          general: {
                            ...formValues.general,
                            authorizationMode: e.target.value
                          }
                        })
                      }}
                      data-testid="authorization-mode-input"
                    />
                  </FormField>
                  {lodashGet(formValues, 'general.authorizationMode') ===
                    authorizationModeEnum.AUTHENTICATION_ONLY_MODE &&
                    lodashGet(formValues, 'general.policyType') ===
                      policyTypeEnum.DOMAIN_VERIFIED && (
                      <Notification
                        margin={{ top: 'small', bottom: 'small' }}
                        status="info"
                        message={
                          <Typography
                            type="text"
                            testId="local-role-notification"
                          >
                            {t(
                              'sso_configurations.create_auth_policy_wizard.local_role_notification'
                            )}
                          </Typography>
                        }
                      />
                    )}
                  {lodashGet(formValues, 'general.authorizationMode') ===
                    authorizationModeEnum.AUTHORIZATION_MODE && (
                    <>
                      <Notification
                        margin={{ top: 'small', bottom: 'small' }}
                        status="info"
                        message={
                          <Typography
                            type="text"
                            testId="sso-role-notification"
                          >
                            {lodashGet(formValues, 'general.policyType') ===
                            policyTypeEnum.DOMAIN_VERIFIED
                              ? t(
                                  'sso_configurations.create_auth_policy_wizard.sso_role_notification_1'
                                )
                              : 'Users whose authorization comes from SSO role assignments cannot be members of user groups where they can receive additional role assignments.  Existing group members subject to this authentication policy will be removed from groups.  Users subject to SSO role assignments will be prohibited from addition to groups, including via external SCIM integrations.'}
                          </Typography>
                        }
                      />
                      <Notification
                        margin={{ top: 'small', bottom: 'small' }}
                        status="info"
                        message={
                          <Typography
                            type="text"
                            testId="sso-role-notification-2"
                          >
                            {lodashGet(formValues, 'general.policyType') ===
                            policyTypeEnum.DOMAIN_VERIFIED
                              ? t(
                                  'sso_configurations.create_auth_policy_wizard.sso_role_notification_2'
                                )
                              : 'Using SSO role assignments for an external authentication policy requires that the protocol used by the connection allows for SSO authorization and requires an ability or arrangement to add authorization to the identity provider.  Without identity provider-specified authorization, users affected by the authentication policy will not be able to access organization workspaces.'}
                          </Typography>
                        }
                      />
                      {lodashGet(formValues, 'general.policyType') ===
                        policyTypeEnum.DOMAIN_EXTERNAL && (
                        <>
                          <Text
                            size="xsmall"
                            margin={{ top: 'xxsmall' }}
                            weight={500}
                          >
                            Acknowledgement*
                          </Text>
                          <FormField
                            htmlFor="acknowledgement"
                            name="acknowledgement"
                            data-testid="acknowledgement-form-field"
                            required
                            error={fieldsError.acknowledgement}
                          >
                            <CheckBoxGroup
                              options={[
                                'I acknowledge that use of SSO authorization requires configuration in the identity provider before users can access organization workspaces.'
                              ]}
                              defaultValue={formValues.general.acknowledgement}
                              onChange={(event) => {
                                setFormValues({
                                  ...formValues,
                                  general: {
                                    ...formValues.general,
                                    acknowledgement: event.value
                                  }
                                })
                              }}
                            />
                          </FormField>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
      {notifMsg &&
        displayNotification(notifMsg, notifSeverity, setNotifMsg, notifTitle)}
    </Box>
  )
}

export default General
